import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { 
  Search, 
  Plus, 
  ArrowLeft, 
  RefreshCw,
  BookOpen,
  Building2,
  Users,
  TrendingUp,
  TrendingDown,
  Wallet,
  Edit,
  Trash2,
  Eye,
  IndianRupee,
  FolderTree,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { api, getErrorMessage } from '../App';

const LedgerManagement = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('ledgers');
  const [ledgers, setLedgers] = useState([]);
  const [accountGroups, setAccountGroups] = useState([]);
  const [filteredLedgers, setFilteredLedgers] = useState([]);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  
  // Dialog states
  const [showLedgerDialog, setShowLedgerDialog] = useState(false);
  const [showGroupDialog, setShowGroupDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingLedger, setEditingLedger] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  
  const [ledgerForm, setLedgerForm] = useState({
    code: '',
    name: '',
    account_group_id: '',
    opening_balance: 0,
    opening_balance_type: 'debit',
    gst_treatment: 'taxable',
    hsn_sac_code: '',
    default_gst_rate: 0,
    is_party_ledger: false,
    party_type: '',
    gstin: '',
    credit_limit: 0,
    credit_days: 0,
    description: ''
  });

  const [groupForm, setGroupForm] = useState({
    code: '',
    name: '',
    account_type: 'assets',
    account_sub_type: '',
    parent_group_id: '',
    description: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterLedgers();
  }, [ledgers, search, typeFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [ledgersRes, groupsRes] = await Promise.all([
        api.get('/finance/ledgers'),
        api.get('/finance/account-groups')
      ]);
      setLedgers(ledgersRes.data);
      setAccountGroups(groupsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const filterLedgers = () => {
    let filtered = [...ledgers];
    
    if (search) {
      filtered = filtered.filter(l => 
        l.name.toLowerCase().includes(search.toLowerCase()) ||
        l.code.toLowerCase().includes(search.toLowerCase())
      );
    }
    
    if (typeFilter !== 'all') {
      filtered = filtered.filter(l => l.account_type === typeFilter);
    }
    
    setFilteredLedgers(filtered);
  };

  // ==================== LEDGER OPERATIONS ====================

  const openLedgerDialog = (ledger = null) => {
    if (ledger) {
      setEditingLedger(ledger);
      setLedgerForm({
        code: ledger.code,
        name: ledger.name,
        account_group_id: ledger.account_group_id,
        opening_balance: ledger.opening_balance || 0,
        opening_balance_type: ledger.opening_balance_type || 'debit',
        gst_treatment: ledger.gst_treatment || 'taxable',
        hsn_sac_code: ledger.hsn_sac_code || '',
        default_gst_rate: ledger.default_gst_rate || 0,
        is_party_ledger: ledger.is_party_ledger || false,
        party_type: ledger.party_type || '',
        gstin: ledger.gstin || '',
        credit_limit: ledger.credit_limit || 0,
        credit_days: ledger.credit_days || 0,
        description: ledger.description || ''
      });
    } else {
      setEditingLedger(null);
      resetLedgerForm();
    }
    setShowLedgerDialog(true);
  };

  const resetLedgerForm = () => {
    setLedgerForm({
      code: '',
      name: '',
      account_group_id: '',
      opening_balance: 0,
      opening_balance_type: 'debit',
      gst_treatment: 'taxable',
      hsn_sac_code: '',
      default_gst_rate: 0,
      is_party_ledger: false,
      party_type: '',
      gstin: '',
      credit_limit: 0,
      credit_days: 0,
      description: ''
    });
  };

  const handleSaveLedger = async () => {
    if (!ledgerForm.code || !ledgerForm.name || !ledgerForm.account_group_id) {
      toast.error('Please fill required fields');
      return;
    }
    
    try {
      if (editingLedger) {
        await api.put(`/finance/ledgers/${editingLedger.id}`, ledgerForm);
        toast.success('Ledger updated successfully');
      } else {
        await api.post('/finance/ledgers', ledgerForm);
        toast.success('Ledger created successfully');
      }
      setShowLedgerDialog(false);
      resetLedgerForm();
      setEditingLedger(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save ledger'));
    }
  };

  const handleDeleteLedger = async (ledger) => {
    setDeleteTarget({ type: 'ledger', item: ledger });
    setShowDeleteDialog(true);
  };

  // ==================== GROUP OPERATIONS ====================

  const openGroupDialog = (group = null) => {
    if (group) {
      setEditingGroup(group);
      setGroupForm({
        code: group.code,
        name: group.name,
        account_type: group.account_type,
        account_sub_type: group.account_sub_type || '',
        parent_group_id: group.parent_group_id || '',
        description: group.description || ''
      });
    } else {
      setEditingGroup(null);
      resetGroupForm();
    }
    setShowGroupDialog(true);
  };

  const resetGroupForm = () => {
    setGroupForm({
      code: '',
      name: '',
      account_type: 'assets',
      account_sub_type: '',
      parent_group_id: '',
      description: ''
    });
  };

  const handleSaveGroup = async () => {
    if (!groupForm.code || !groupForm.name || !groupForm.account_type) {
      toast.error('Please fill required fields');
      return;
    }
    
    try {
      if (editingGroup) {
        await api.put(`/finance/account-groups/${editingGroup.id}`, groupForm);
        toast.success('Account group updated successfully');
      } else {
        await api.post('/finance/account-groups', groupForm);
        toast.success('Account group created successfully');
      }
      setShowGroupDialog(false);
      resetGroupForm();
      setEditingGroup(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save account group'));
    }
  };

  const handleDeleteGroup = async (group) => {
    setDeleteTarget({ type: 'group', item: group });
    setShowDeleteDialog(true);
  };

  // ==================== DELETE CONFIRMATION ====================

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    
    try {
      if (deleteTarget.type === 'ledger') {
        await api.delete(`/finance/ledgers/${deleteTarget.item.id}`);
        toast.success('Ledger deleted successfully');
      } else if (deleteTarget.type === 'group') {
        await api.delete(`/finance/account-groups/${deleteTarget.item.id}`);
        toast.success('Account group deleted successfully');
      }
      setShowDeleteDialog(false);
      setDeleteTarget(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete'));
    }
  };

  // ==================== UTILITIES ====================

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'assets': return <TrendingUp className="h-4 w-4 text-green-600" />;
      case 'liabilities': return <TrendingDown className="h-4 w-4 text-red-600" />;
      case 'equity': return <Building2 className="h-4 w-4 text-purple-600" />;
      case 'income': return <Wallet className="h-4 w-4 text-blue-600" />;
      case 'expenses': return <IndianRupee className="h-4 w-4 text-orange-600" />;
      default: return <BookOpen className="h-4 w-4" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'assets': return 'bg-green-100 text-green-700 border-green-200';
      case 'liabilities': return 'bg-red-100 text-red-700 border-red-200';
      case 'equity': return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'income': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'expenses': return 'bg-orange-100 text-orange-700 border-orange-200';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const groupedLedgers = filteredLedgers.reduce((acc, ledger) => {
    const group = accountGroups.find(g => g.id === ledger.account_group_id);
    const groupName = group?.name || 'Ungrouped';
    if (!acc[groupName]) acc[groupName] = [];
    acc[groupName].push(ledger);
    return acc;
  }, {});

  // Group hierarchy for display
  const groupsByType = accountGroups.reduce((acc, group) => {
    const type = group.account_type;
    if (!acc[type]) acc[type] = [];
    acc[type].push(group);
    return acc;
  }, {});

  // Summary stats
  const stats = {
    total: ledgers.length,
    assets: ledgers.filter(l => l.account_type === 'assets').length,
    liabilities: ledgers.filter(l => l.account_type === 'liabilities').length,
    income: ledgers.filter(l => l.account_type === 'income').length,
    expenses: ledgers.filter(l => l.account_type === 'expenses').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/finance')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Chart of Accounts</h1>
            <p className="text-gray-600">{ledgers.length} ledgers in {accountGroups.length} groups</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="ledgers" className="flex items-center gap-2">
            <BookOpen className="h-4 w-4" />
            Ledgers
          </TabsTrigger>
          <TabsTrigger value="groups" className="flex items-center gap-2">
            <FolderTree className="h-4 w-4" />
            Account Groups
          </TabsTrigger>
        </TabsList>

        {/* Ledgers Tab */}
        <TabsContent value="ledgers" className="space-y-4">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="cursor-pointer hover:shadow-md" onClick={() => setTypeFilter('all')}>
              <CardContent className="pt-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                  <p className="text-sm text-gray-600">Total Ledgers</p>
                </div>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:shadow-md bg-green-50" onClick={() => setTypeFilter('assets')}>
              <CardContent className="pt-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-700">{stats.assets}</p>
                  <p className="text-sm text-green-600">Assets</p>
                </div>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:shadow-md bg-red-50" onClick={() => setTypeFilter('liabilities')}>
              <CardContent className="pt-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-700">{stats.liabilities}</p>
                  <p className="text-sm text-red-600">Liabilities</p>
                </div>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:shadow-md bg-blue-50" onClick={() => setTypeFilter('income')}>
              <CardContent className="pt-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-700">{stats.income}</p>
                  <p className="text-sm text-blue-600">Income</p>
                </div>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:shadow-md bg-orange-50" onClick={() => setTypeFilter('expenses')}>
              <CardContent className="pt-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-orange-700">{stats.expenses}</p>
                  <p className="text-sm text-orange-600">Expenses</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Search and Actions */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search ledgers..."
                className="pl-10"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="assets">Assets</SelectItem>
                <SelectItem value="liabilities">Liabilities</SelectItem>
                <SelectItem value="equity">Equity</SelectItem>
                <SelectItem value="income">Income</SelectItem>
                <SelectItem value="expenses">Expenses</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={() => openLedgerDialog()}>
              <Plus className="h-4 w-4 mr-2" />
              New Ledger
            </Button>
          </div>

          {/* Ledgers List */}
          <div className="space-y-6">
            {Object.entries(groupedLedgers).map(([groupName, groupLedgers]) => (
              <Card key={groupName}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-medium flex items-center gap-2">
                    <BookOpen className="h-5 w-5 text-gray-400" />
                    {groupName}
                    <Badge variant="outline" className="ml-2">{groupLedgers.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="divide-y">
                    {groupLedgers.map((ledger) => (
                      <div 
                        key={ledger.id}
                        className="py-3 flex items-center justify-between hover:bg-gray-50 px-2 rounded"
                      >
                        <div className="flex items-center gap-4">
                          <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                            ledger.account_type === 'assets' ? 'bg-green-100' :
                            ledger.account_type === 'liabilities' ? 'bg-red-100' :
                            ledger.account_type === 'income' ? 'bg-blue-100' :
                            ledger.account_type === 'expenses' ? 'bg-orange-100' :
                            'bg-purple-100'
                          }`}>
                            {getTypeIcon(ledger.account_type)}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900">{ledger.name}</span>
                              <span className="text-sm text-gray-500">({ledger.code})</span>
                              {ledger.is_system && (
                                <Badge variant="outline" className="text-xs bg-gray-100">System</Badge>
                              )}
                              {ledger.is_party_ledger && (
                                <Badge variant="outline" className="text-xs">
                                  <Users className="h-3 w-3 mr-1" />
                                  {ledger.party_type}
                                </Badge>
                              )}
                            </div>
                            {ledger.description && (
                              <p className="text-sm text-gray-500 truncate max-w-md">{ledger.description}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-right mr-4">
                            <p className={`font-medium ${
                              ledger.current_balance >= 0 ? 'text-gray-900' : 'text-red-600'
                            }`}>
                              {formatCurrency(Math.abs(ledger.current_balance + ledger.opening_balance))}
                            </p>
                            <p className="text-xs text-gray-500">
                              {ledger.current_balance + ledger.opening_balance >= 0 ? 'Dr' : 'Cr'}
                            </p>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => openLedgerDialog(ledger)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          {!ledger.is_system && (
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleDeleteLedger(ledger)}
                              disabled={ledger.opening_balance !== 0 || ledger.current_balance !== 0}
                              title={ledger.opening_balance !== 0 || ledger.current_balance !== 0 
                                ? "Cannot delete ledger with balance" 
                                : "Delete ledger"}
                            >
                              <Trash2 className={`h-4 w-4 ${
                                ledger.opening_balance !== 0 || ledger.current_balance !== 0 
                                  ? 'text-gray-300' 
                                  : 'text-red-500'
                              }`} />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Account Groups Tab */}
        <TabsContent value="groups" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => openGroupDialog()}>
              <Plus className="h-4 w-4 mr-2" />
              New Account Group
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {['assets', 'liabilities', 'equity', 'income', 'expenses'].map(type => (
              <Card key={type}>
                <CardHeader className="pb-3">
                  <CardTitle className={`text-lg font-medium flex items-center gap-2 capitalize ${getTypeColor(type).split(' ')[1]}`}>
                    {getTypeIcon(type)}
                    {type}
                    <Badge variant="outline" className="ml-2">{groupsByType[type]?.length || 0}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {groupsByType[type]?.length > 0 ? (
                    <div className="space-y-2">
                      {groupsByType[type].map(group => {
                        const ledgerCount = ledgers.filter(l => l.account_group_id === group.id).length;
                        return (
                          <div 
                            key={group.id}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-sm text-gray-500 font-mono">{group.code}</span>
                              <span className="font-medium">{group.name}</span>
                              {group.is_system && (
                                <Badge variant="outline" className="text-xs bg-gray-100">System</Badge>
                              )}
                              <Badge variant="outline" className="text-xs">{ledgerCount} ledgers</Badge>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => openGroupDialog(group)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              {!group.is_system && (
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  onClick={() => handleDeleteGroup(group)}
                                >
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm text-center py-4">No groups in this category</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Ledger Dialog */}
      <Dialog open={showLedgerDialog} onOpenChange={setShowLedgerDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingLedger ? 'Edit Ledger' : 'Create New Ledger'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="code">Ledger Code *</Label>
                <Input
                  id="code"
                  value={ledgerForm.code}
                  onChange={(e) => setLedgerForm({...ledgerForm, code: e.target.value})}
                  placeholder="e.g., 1114"
                  disabled={editingLedger?.is_system}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Ledger Name *</Label>
                <Input
                  id="name"
                  value={ledgerForm.name}
                  onChange={(e) => setLedgerForm({...ledgerForm, name: e.target.value})}
                  placeholder="e.g., Main Bank Account"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="account_group">Account Group *</Label>
              <Select 
                value={ledgerForm.account_group_id} 
                onValueChange={(value) => setLedgerForm({...ledgerForm, account_group_id: value})}
                disabled={editingLedger?.is_system}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select account group" />
                </SelectTrigger>
                <SelectContent>
                  {accountGroups.map(group => (
                    <SelectItem key={group.id} value={group.id}>
                      {group.code} - {group.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="opening_balance">Opening Balance</Label>
                <Input
                  id="opening_balance"
                  type="number"
                  value={ledgerForm.opening_balance}
                  onChange={(e) => setLedgerForm({...ledgerForm, opening_balance: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="opening_balance_type">Balance Type</Label>
                <Select 
                  value={ledgerForm.opening_balance_type} 
                  onValueChange={(value) => setLedgerForm({...ledgerForm, opening_balance_type: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="debit">Debit (Dr)</SelectItem>
                    <SelectItem value="credit">Credit (Cr)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>
                <input 
                  type="checkbox"
                  checked={ledgerForm.is_party_ledger}
                  onChange={(e) => setLedgerForm({...ledgerForm, is_party_ledger: e.target.checked})}
                  className="mr-2"
                />
                This is a Party Ledger (Customer/Vendor)
              </Label>
            </div>

            {ledgerForm.is_party_ledger && (
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="space-y-2">
                  <Label htmlFor="party_type">Party Type</Label>
                  <Select 
                    value={ledgerForm.party_type} 
                    onValueChange={(value) => setLedgerForm({...ledgerForm, party_type: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="customer">Customer</SelectItem>
                      <SelectItem value="vendor">Vendor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gstin">GSTIN</Label>
                  <Input
                    id="gstin"
                    value={ledgerForm.gstin}
                    onChange={(e) => setLedgerForm({...ledgerForm, gstin: e.target.value.toUpperCase()})}
                    placeholder="22AAAAA0000A1Z5"
                    maxLength={15}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="credit_limit">Credit Limit</Label>
                  <Input
                    id="credit_limit"
                    type="number"
                    value={ledgerForm.credit_limit}
                    onChange={(e) => setLedgerForm({...ledgerForm, credit_limit: parseFloat(e.target.value) || 0})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="credit_days">Credit Days</Label>
                  <Input
                    id="credit_days"
                    type="number"
                    value={ledgerForm.credit_days}
                    onChange={(e) => setLedgerForm({...ledgerForm, credit_days: parseInt(e.target.value) || 0})}
                  />
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="gst_treatment">GST Treatment</Label>
                <Select 
                  value={ledgerForm.gst_treatment} 
                  onValueChange={(value) => setLedgerForm({...ledgerForm, gst_treatment: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="taxable">Taxable</SelectItem>
                    <SelectItem value="exempt">Exempt</SelectItem>
                    <SelectItem value="nil_rated">Nil Rated</SelectItem>
                    <SelectItem value="non_gst">Non-GST</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="default_gst_rate">Default GST Rate (%)</Label>
                <Select 
                  value={String(ledgerForm.default_gst_rate)} 
                  onValueChange={(value) => setLedgerForm({...ledgerForm, default_gst_rate: parseFloat(value)})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">0%</SelectItem>
                    <SelectItem value="5">5%</SelectItem>
                    <SelectItem value="12">12%</SelectItem>
                    <SelectItem value="18">18%</SelectItem>
                    <SelectItem value="28">28%</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={ledgerForm.description}
                onChange={(e) => setLedgerForm({...ledgerForm, description: e.target.value})}
                placeholder="Optional description"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLedgerDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveLedger}>
              {editingLedger ? 'Update' : 'Create'} Ledger
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Account Group Dialog */}
      <Dialog open={showGroupDialog} onOpenChange={setShowGroupDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingGroup ? 'Edit Account Group' : 'Create New Account Group'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="group_code">Group Code *</Label>
                <Input
                  id="group_code"
                  value={groupForm.code}
                  onChange={(e) => setGroupForm({...groupForm, code: e.target.value})}
                  placeholder="e.g., 1150"
                  disabled={editingGroup?.is_system}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="group_name">Group Name *</Label>
                <Input
                  id="group_name"
                  value={groupForm.name}
                  onChange={(e) => setGroupForm({...groupForm, name: e.target.value})}
                  placeholder="e.g., Other Current Assets"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="account_type">Account Type *</Label>
              <Select 
                value={groupForm.account_type} 
                onValueChange={(value) => setGroupForm({...groupForm, account_type: value})}
                disabled={editingGroup?.is_system}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="assets">Assets</SelectItem>
                  <SelectItem value="liabilities">Liabilities</SelectItem>
                  <SelectItem value="equity">Equity</SelectItem>
                  <SelectItem value="income">Income</SelectItem>
                  <SelectItem value="expenses">Expenses</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="parent_group">Parent Group (Optional)</Label>
              <Select 
                value={groupForm.parent_group_id || "none"} 
                onValueChange={(value) => setGroupForm({...groupForm, parent_group_id: value === "none" ? "" : value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select parent group" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Parent (Top Level)</SelectItem>
                  {accountGroups
                    .filter(g => g.account_type === groupForm.account_type && g.id !== editingGroup?.id)
                    .map(group => (
                      <SelectItem key={group.id} value={group.id}>
                        {group.code} - {group.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="group_description">Description</Label>
              <Input
                id="group_description"
                value={groupForm.description}
                onChange={(e) => setGroupForm({...groupForm, description: e.target.value})}
                placeholder="Optional description"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGroupDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveGroup}>
              {editingGroup ? 'Update' : 'Create'} Group
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {deleteTarget?.type === 'ledger' ? 'ledger' : 'account group'}{' '}
              <strong>{deleteTarget?.item?.name}</strong>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LedgerManagement;
